import { View } from 'react-native';
import Alert from 'utils/packages/Alert';
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { FlashList } from '@shopify/flash-list';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, SCROLL_INDICATOR_INSETS, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector } from 'store/hooks';
import getListOfflineAccounts from 'utils/api/getListOfflineAccounts';
import { selectActiveConnections } from 'reducers/selectors';
import { SelectableAccountCard } from 'design-system/SelectableAccountCard';
import { sortAccountsByType } from 'utils/api/sortAccounts';
import FlatListItemCard from 'design-system/FlatListItemCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
const strings = {
    title: 'Select your linked bank',
    subtitle: 'We’ll use this to detect any incoming or outgoing transaction from or to an offline account',
};
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const SmartRuleSelectAccountScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const activeConnections = useAppSelector(selectActiveConnections);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    });
    const onPress = useCallback((data) => {
        if (!data) {
            return;
        }
        setSelectedAccounts((prev) => {
            let array = [...prev];
            const index = array.indexOf(data.id);
            if (index === -1) {
                array.push(data.id);
            }
            else {
                array = array.filter((item) => item !== data.id);
            }
            return array;
        });
    }, []);
    const accounts = useMemo(() => {
        const accounts = getListOfflineAccounts(activeConnections, true);
        return sortAccountsByType(accounts).filter((acc) => !acc.isHidden);
    }, [activeConnections]);
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index} style={styles.card} onPress={() => onPress(item)} itemsLength={accounts.length}>
            <View pointerEvents="none">
              <SelectableAccountCard data={item} transparent showHiddenLogo isSelected={selectedAccounts.indexOf(item.id) > -1}/>
            </View>
          </FlatListItemCard>), [accounts.length, onPress, selectedAccounts, styles.card]);
    const handleContinue = useCallback(() => {
        if (!selectedAccounts.length) {
            Alert.alert('Hold on 💫', 'You need to select an account.');
            return;
        }
        navigation.navigateDeprecated('SmartRuleFilters', {
            name: route.params.name,
            selectedAccounts,
        });
    }, [route.params.name, selectedAccounts]);
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader title={strings.title} subTitle={strings.subtitle}/>), []);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <AnimatedFlashList extraData={[selectedAccounts, colors.cards.onDark]} data={accounts} renderItem={renderItem} keyExtractor={(i) => i.id.toString()} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom} ListHeaderComponent={ListHeaderComponent}/>
          <ButtonGroup type="floating">
            <Button brand title="Continue" onPress={handleContinue}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default SmartRuleSelectAccountScreen;
